import { Box } from "ui/box";
import { Switch } from "ui/switch";
import FilterPanel from "components/partials/filter-panel/filter-panel";

import {
  mapDictionaryItemsToMultiSelectOptions,
  useGetPopulationTypesQuery,
  useGetCampaignClassTypesQuery,
  useGetProgramTypesQuery,
  useGetTouchpointContentTypesQuery,
} from "hooks/api/dictionary";
import { useCurrentUser } from "hooks/api/use-current-user";

import { createCheckboxArray, createCreativeTypeCheckboxArray } from "utilities";
import { hasPermission } from "utilities/user";

import { INBOX_FILTER_IDS } from "containers/admin/inbox/inbox.consts";

import { TouchpointStatuses } from "types";
import { TouchpointType } from "types/touchpoint";
import { Permission } from "types/auth";
import { QuickFilterTypes } from "types/inbox";
import { Filter, FilterOption } from "types/filters";
import { FiltersToApplyOnInbox } from "containers/admin/inbox/inbox.types";

import { InboxAllFiltersProps } from "./inbox-all-filters.types";

const StatusesForInboxFilter = {
  DRAFT: TouchpointStatuses.DRAFT,
  CLIENT_REVIEW: TouchpointStatuses.CLIENT_REVIEW,
  ARCHIVED_CLIENT_REVIEW: TouchpointStatuses.ARCHIVED_CLIENT_REVIEW,
  REJECTED: TouchpointStatuses.REJECTED,
  SYSTEM_DRAFT: TouchpointStatuses.SYSTEM_DRAFT,
  APPROVED: TouchpointStatuses.APPROVED,
  PUBLISHED: TouchpointStatuses.PUBLISHED,
};

const COMMENTS_OPTIONS = [
  { name: "has-open-comments", description: "Has open comments" },
  { name: "has-action-items", description: "Has action items" },
];

export default function InboxAllFilters({
  isLoading,
  filtersToApply,
  setFiltersToApply,
}: InboxAllFiltersProps) {
  const { data: contentTypes = [] } = useGetTouchpointContentTypesQuery();
  const { data: populationTypes = [] } = useGetPopulationTypesQuery();
  const { data: classTypes = [] } = useGetCampaignClassTypesQuery();
  const { data: programTypes = [] } = useGetProgramTypesQuery();

  const classOptions = mapDictionaryItemsToMultiSelectOptions(classTypes).map((o) => ({
    name: String(o.value),
    description: o.label,
  }));

  const currentUser = useCurrentUser();
  const isClientUser = hasPermission(currentUser, Permission.PERM_CLIENT_USER);
  const isStaffUser = !isClientUser;

  // Quick filter (staff-only): "Has open action items" toggle.
  const quickFilters = filtersToApply[INBOX_FILTER_IDS.quickFilters] ?? [];
  const isHasOpenActionItemsActive = quickFilters.includes(QuickFilterTypes.hasOpenActionItems);

  const handleHasOpenActionItemsToggle = () => {
    const next = isHasOpenActionItemsActive
      ? quickFilters.filter((q) => q !== QuickFilterTypes.hasOpenActionItems)
      : [...quickFilters, QuickFilterTypes.hasOpenActionItems];

    setFiltersToApply({
      ...filtersToApply,
      [INBOX_FILTER_IDS.quickFilters]: next,
    });
  };

  const filtersToDisplay: Filter[] = [
    // Available to both client and staff users
    {
      filterId: INBOX_FILTER_IDS.creativeType,
      title: "Channel",
      options: createCreativeTypeCheckboxArray(TouchpointType)
        .filter((item) => item.value !== TouchpointType.THEME)
        .map((o) => ({
          name: String(o.value),
          description: o.label,
        })),
    },
    {
      filterId: INBOX_FILTER_IDS.populationType,
      title: "Population type",
      options: mapDictionaryItemsToMultiSelectOptions(populationTypes).map((o) => ({
        name: String(o.value),
        description: o.label,
      })),
    },
    {
      filterId: INBOX_FILTER_IDS.classes,
      title: "Class",
      options: classOptions,
    },
    // Staff-only filters
    ...(isStaffUser
      ? [
          {
            filterId: INBOX_FILTER_IDS.clientReviewStatus,
            title: "Client review status",
            options: createCheckboxArray(StatusesForInboxFilter).map((o) => ({
              name: String(o.value),
              description: o.label,
            })),
          },
          {
            filterId: INBOX_FILTER_IDS.contentTypes,
            title: "Content types",
            options: mapDictionaryItemsToMultiSelectOptions(contentTypes).map((o) => ({
              name: String(o.value),
              description: o.label,
            })),
          },
          {
            filterId: INBOX_FILTER_IDS.comments,
            title: "Comments",
            options: COMMENTS_OPTIONS,
          },
        ]
      : []),
    // The legacy enum included `programType` for client users via `ClientFilterTypes`,
    // but it had no `case` in `findFilterOptions` for staff users either. Preserve that
    // behavior: surface program-type only for client users.
    ...(isClientUser
      ? [
          {
            filterId: INBOX_FILTER_IDS.programType,
            title: "Program type",
            options: mapDictionaryItemsToMultiSelectOptions(programTypes).map((o) => ({
              name: String(o.value),
              description: o.label,
            })),
          },
        ]
      : []),
  ];

  const handleFilterPanelChange = (filter: Filter, option: FilterOption) => {
    const filterKey = filter.filterId as keyof FiltersToApplyOnInbox;
    const currentValues = (filtersToApply[filterKey] as string[] | undefined) ?? [];
    const isSelected = currentValues.includes(option.name);

    setFiltersToApply({
      ...filtersToApply,
      [filterKey]: isSelected
        ? currentValues.filter((v) => v !== option.name)
        : [...currentValues, option.name],
    });
  };

  return (
    <FilterPanel
      filters={filtersToDisplay}
      currentFilters={filtersToApply}
      onFilterChange={handleFilterPanelChange}
      isFetching={isLoading}
      beforeFilters={
        isStaffUser && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              // `ui/switch` shrink-wraps its label row. Stretching it here — rather than in the
              // primitive, which 30-odd call sites share — puts the label on the panel's left edge
              // and the toggle on its right.
              "& .MuiFormControlLabel-root": {
                flexGrow: 1,
                justifyContent: "space-between",
              },
            }}>
            <Switch
              toggleName="Has open action items"
              checked={isHasOpenActionItemsActive}
              disabled={isLoading}
              onChange={() => handleHasOpenActionItemsToggle()}
              labelPlacement="start"
              labelSx={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
            />
          </Box>
        )
      }
    />
  );
}
